import './App.css';
import { useContext } from './Context';
import ImageMatch from './components/ImageMatch';
import ImageUpload from './components/ImageUpload';
import React, { useState, useEffect } from 'react';
import SideDrawer from './components/SideDrawer';
import FilterButtons from './components/FilterButtons';
import logo from './materialised-logo-white.png';
import Icon from './icons/icon';
import Footer from './components/Footer';
import Toast from './components/Toast';
import InstallPrompt from './components/InstallPrompt';

const App = () => {
  const { globalState, setGlobalState } = useContext();
  const { drawerOpen, devMode, showSettings } = globalState;

  const [keyPressCount, setKeyPressCount] = useState(0);
  const [timer, setTimer] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIos, setIsIos] = useState(false);

  // Listen for service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
                setShowToast(true); // Show the toast indefinitely
              }
            });
          }
        });
      });
    }
  }, []);

  const handleUpdate = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          registration.waiting.addEventListener('statechange', (event) => {
            if ((event.target).state === 'activated') {
              window.location.reload(); // Reload to apply the update
            }
          });
        }
      });
    }
  };

  const handleCloseToast = () => {
    setShowToast(false); // Dismiss the toast
  };

  // Detect iOS and Safari to show custom instructions
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    console.log(isIosDevice);
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;

    if (isIosDevice && !isInStandaloneMode) {
      setIsIos(isIosDevice && !isInStandaloneMode); // Only show for iOS if not already installed
      setShowInstallPrompt(true);
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault(); // Prevent default behavior
      setDeferredPrompt(event.detail || event); // Support simulated events
      setShowInstallPrompt(true);
      console.log('DeferredPrompt set:', event.detail || event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt && typeof deferredPrompt.prompt === 'function') {
      deferredPrompt.prompt(); // Show the native install prompt
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt.');
        } else {
          console.log('User dismissed the install prompt.');
        }
        setDeferredPrompt(null); // Clear the deferred prompt
        setShowInstallPrompt(false); // Hide the custom prompt
      });
    } else {
      console.error('DeferredPrompt is not available or not a function.');
    }
  };

  const handleCancelInstall = () => {
    setShowInstallPrompt(false); // Hide the custom install prompt
  };

  // Handle key press for toggling settings
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 's' || event.key === 'S') {
        setKeyPressCount((prevCount) => prevCount + 1);

        if (keyPressCount === 0) {
          setTimer(
            setTimeout(() => {
              setKeyPressCount(0); // Reset after 2 seconds
            }, 2000)
          );
        } else if (keyPressCount === 1) {
          setGlobalState((prevState) => ({ ...prevState, showSettings: !showSettings }));
          clearTimeout(timer); // Clear the timer
          setKeyPressCount(0); // Reset count
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      clearTimeout(timer); // Clear timer on unmount
    };
  }, [keyPressCount, timer, setGlobalState, showSettings]);

  return (
    <div className="App">
      <header className="App-header">
        <a
          href="https://materialised.com.au/"
          target="_blank"
          rel="noreferrer"
          className="app-logo-container"
        >
          <img src={logo} className="app-logo my2" alt="materialised logo" />
        </a>

        <FilterButtons />

        <div className="grid grid-2 container">
          <ImageUpload />
          <ImageMatch />

          {devMode && (
            <div className="center">
              <Icon icon="dropbox" />
              <Icon icon="drive" />
            </div>
          )}
        </div>

        {showSettings && (
          <button
            className="nav cursor-pointer"
            onClick={() => setGlobalState({ ...globalState, drawerOpen: !drawerOpen })}
          >
            settings
          </button>
        )}
        <SideDrawer />
      </header>

      <Footer />

      {showToast && (
        <Toast
          message="A new version is available!"
          onUpdate={handleUpdate}
          onClose={handleCloseToast}
        />
      )}

      {showInstallPrompt && (
        <InstallPrompt
          onInstall={handleInstallClick}
          onCancel={handleCancelInstall}
          isIos={isIos}
        />
      )}
    </div>
  );
};

export default App;
